import TextField from "@mui/material/TextField";
import { TextFieldVariants } from "@mui/material";
import { styled } from "@mui/material/styles";
import { forwardRef, useState, useEffect } from "react";
import Year, { InvalidYearError } from "_shared/dates/years/year";

const InputField = styled(TextField)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    marginRight: 0,
    width: '100%'
  },
  marginBottom: theme.spacing(1.5),
  minWidth: theme.spacing(40),
  ":first-of-type": {
    marginRight: theme.spacing(1.5)
  }
}));

type YearInputProps = {
  className?: string;
  variant?: TextFieldVariants;
  year?: Year;
  cannotBeInPast?: boolean;
  cannotBeInFuture?: boolean;
  label?: string;
  required?: boolean;
  onChange?: (year?: Year, error?: string) => void;
  onKeyDown?: (event: React.KeyboardEvent<HTMLDivElement>) => void;
};

const YearInput = forwardRef(
  function YearInput(props: YearInputProps, _ref) {
  const { 
    className, 
    variant, 
    year, 
    cannotBeInPast, 
    cannotBeInFuture, 
    label, 
    required,
    onChange, 
    onKeyDown 
  } = props;

  const [yearFieldText, setYearFieldText] = useState<string>(year?.string ?? '');
  const [yearFieldError, setYearFieldError] = useState<string>('');

  useEffect(() => {
    setYearFieldText(year?.string ?? '');
  }, [year]);

  function buildYear(value?: string): Year | undefined {
    try {
      if (!value) throw new InvalidYearError({errorMessage: "Year is required", value: value ?? ''});
      return new Year(value, { cannotBeInPast, cannotBeInFuture });
    } catch (error) {
      if (error instanceof InvalidYearError) {
        throw error;
      }
    }
  }

  function handleYearChanged(inputYear?: string) {
    let newYear: Year | undefined;
    let newErrors: string = '';

    try {
      newYear = buildYear(inputYear);
      setYearFieldError('');
    } catch (error) {
      if (!(error instanceof InvalidYearError)) return;
      newErrors = error.message;
      setYearFieldError(newErrors);
    } finally {
      onChange?.(newYear, newErrors);
    }
  }

  return (
    <InputField
      className={className}
      label={label ?? "Year"}
      onChange={(event) => {
        setYearFieldText(event.target.value);
        handleYearChanged(event.target.value);
      }}
      required={required}
      value={yearFieldText}
      error={!!yearFieldError}
      helperText={yearFieldError}
      variant={variant}
      onKeyDown={onKeyDown}
    />
  );
});

export default YearInput;
